import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import api from "@/lib/api";
import { PublicLayout } from "@/components/layout/PublicLayout";

export default function Home() {
  const [properties, setProperties] = useState([]);
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
    api.get("/experiences").then((r) => setExperiences(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1565880429858-ed7cb461f1b0?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Aurelia Singapore"
          className="kenburns absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-24 lg:px-10">
          <p className="eyebrow fade-up">Marina Bay · Sentosa · Orchard · The Quay · Singapore</p>
          <h1 className="fade-up mt-6 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "0.1s" }}>
            Sanctuaries of rare elegance & timeless hospitality
          </h1>
          <p className="fade-up mt-6 max-w-xl font-sans text-base leading-relaxed text-[var(--aur-text-2)] sm:text-lg" style={{ animationDelay: "0.2s" }}>
            An intimate collection of ultra-luxury estates, each a world unto itself. Every stay begins
            with a personal request, confirmed by hand.
          </p>
          <div className="fade-up mt-10 flex flex-wrap gap-4" style={{ animationDelay: "0.3s" }}>
            <Link to="/properties" data-testid="hero-explore-btn" className="group flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] transition-all hover:bg-gold-bright">
              Explore Properties <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/careers" data-testid="hero-careers-btn" className="rounded-full border border-white/25 px-7 py-3.5 font-sans text-xs font-semibold uppercase tracking-widest text-white transition-all hover:border-gold hover:text-gold">
              Join Our Team
            </Link>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="mx-auto max-w-4xl px-6 py-28 text-center lg:px-10">
        <p className="eyebrow">The Aurelia Philosophy</p>
        <h2 className="mt-6 font-serif text-2xl leading-snug tracking-tight sm:text-3xl lg:text-4xl">
          We do not sell rooms. We compose stays — considered, unhurried, and entirely yours.
        </h2>
        <p className="mx-auto mt-8 max-w-2xl font-sans text-base leading-relaxed text-[var(--aur-text-2)]">
          Across Singapore's most storied addresses, our hotels share one belief: that true luxury is quiet,
          personal, and impossible to automate. That is why every reservation is confirmed by a human hand.
        </p>
      </section>

      {/* Properties */}
      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <p className="eyebrow">The Collection</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">Four addresses, one standard</h2>
          </div>
          <Link to="/properties" className="hidden font-mono text-[11px] uppercase tracking-widest text-gold hover:text-gold-bright md:block">
            View all →
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {properties.map((p, i) => (
            <Link
              key={p.id}
              to={`/properties/${p.id}`}
              data-testid={`property-card-${p.slug}`}
              className="group relative block overflow-hidden rounded-lg border border-[var(--aur-border)]"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={p.hero_image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 hero-overlay" />
              <div className="absolute bottom-0 left-0 p-7">
                <div className="mb-2 flex items-center gap-2 text-[var(--aur-text-2)]">
                  <MapPin size={13} className="text-gold" />
                  <span className="font-mono text-[10px] uppercase tracking-widest">{p.city}, {p.country}</span>
                </div>
                <h3 className="font-serif text-2xl tracking-tight">{p.name}</h3>
                <p className="mt-1 font-sans text-sm text-[var(--aur-text-2)]">{p.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Experiences */}
      <section className="border-t border-[var(--aur-border)] bg-[var(--aur-bg-2)] py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-12 text-center">
            <p className="eyebrow">Beyond the Room</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">Experiences woven into every stay</h2>
            <p className="mx-auto mt-4 max-w-xl font-sans text-base text-[var(--aur-text-2)]">
              Spa sanctuaries, celebrated dining, rooftop pools, weddings, wellness, and conferences — the fullness of Aurelia.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {experiences.map((e) => (
              <div key={e.id} data-testid={`experience-${e.id}`} className="group relative overflow-hidden rounded-lg border border-[var(--aur-border)]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={e.image} alt={e.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-serif text-xl">{e.title}</h3>
                  <p className="mt-1 font-sans text-sm text-[var(--aur-text-2)]">{e.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
